import { useState } from 'react';

import { CheckCircle2, PlayCircle, Plus, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  AVISO_DE_CADASTRO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { formatarDinheiro } from '@/nucleo/dinheiro';
import { formatarData } from '@/nucleo/formato/datas';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Spinner } from '@/ui/Spinner';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';

import { useReajustes, useSimulacaoDeReajuste } from './dados';

/**
 * `/admin/parametros/reajuste-anual` — o IPCA/IPC-FIPE anual que versiona a
 * tabela de preços e os valores base.
 *
 * "NOVO ÍNDICE" ABRE O FORMULÁRIO, E "SIMULAR" ABRE A PRÉVIA — MAS NENHUM DOS
 * DOIS GRAVA NADA. `/reajuste-anual/*` não existe na API
 * (`apps/api/src/modulos/` só tem `eu`, `govbr` e `senha`), e isto não é um dos
 * cinco módulos de cadastro cobertos pela exceção de mock temporário de leitura
 * registrada em CLAUDE.md — não há linha inventada aqui, nem para a listagem de
 * índices nem para a simulação: as duas consultas falham de verdade, e a tela
 * mostra isso pelo `ErrorState`, com "Tentar de novo".
 *
 * O botão "Cadastrar" e o botão "Aplicar reajuste N" mostram o aviso honesto de
 * indisponibilidade em vez de fingir sucesso — mesmo precedente de
 * `CondutoresPage`, "Novo Condutor": a interface fica pronta antes do backend, e
 * o "Salvar" nunca finge que gravou o que não gravou
 * (`modulos/operacao/comum/cadastroIndisponivel.ts`).
 */
export function ReajusteAnualPage() {
  const { data: reajustes, isLoading, isError, error, refetch } = useReajustes();
  const [novoAberto, setNovoAberto] = useState(false);
  const [anoSimulacao, setAnoSimulacao] = useState<number | null>(null);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reajuste anual (IPCA / IPC-FIPE)"
        description="Versiona automaticamente a tabela de preços e os valores base aplicando o índice oficial."
      >
        <Button onClick={() => setNovoAberto(true)}>
          <Plus className="h-4 w-4 sm:mr-2" aria-hidden="true" />
          <span className="hidden sm:inline">Novo índice</span>
        </Button>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Índices cadastrados</CardTitle>
          <CardDescription>
            O simulador é não destrutivo; o aplicador encerra a vigência das tabelas atuais e cria
            novas versões.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isError ? (
            <ErrorState
              variant="load-failed"
              description={
                error?.aviso ??
                'Não foi possível buscar os índices de reajuste agora. Verifique sua conexão e tente novamente.'
              }
              primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
            />
          ) : isLoading ? (
            <Spinner />
          ) : !reajustes || reajustes.length === 0 ? (
            <EmptyState
              icon={TrendingUp}
              title="Nenhum índice cadastrado"
              description="Registre o IPCA/IPC-FIPE anual para versionar os preços."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ano</TableHead>
                  <TableHead>Índice</TableHead>
                  <TableHead>Fonte</TableHead>
                  <TableHead>Decreto</TableHead>
                  <TableHead>Vigência</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Aplicado</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reajustes.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.ano}</TableCell>
                    <TableCell>{Number(r.indice_pct).toFixed(4)}%</TableCell>
                    <TableCell>{r.fonte}</TableCell>
                    <TableCell className="text-xs">{r.decreto_referencia ?? '—'}</TableCell>
                    <TableCell>{formatarData(r.vigencia_inicio)}</TableCell>
                    <TableCell>
                      <Badge variant={r.status === 'APLICADO' ? 'secondary' : 'default'}>
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.aplicado_em ? (
                        <>
                          {r.tabela_precos_geradas} tabelas · {r.valores_base_gerados} bases
                        </>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {r.status === 'PENDENTE' && (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setAnoSimulacao(r.ano)}
                          >
                            <PlayCircle className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Simular
                          </Button>
                        </div>
                      )}
                      {r.status === 'APLICADO' && (
                        <span className="inline-flex items-center gap-1 text-xs text-success">
                          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> Aplicado
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <NovoIndiceDialog open={novoAberto} onOpenChange={setNovoAberto} />

      {anoSimulacao !== null && (
        <SimulacaoDialog ano={anoSimulacao} onClose={() => setAnoSimulacao(null)} />
      )}
    </div>
  );
}

interface NovoIndiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function NovoIndiceDialog({ open, onOpenChange }: NovoIndiceDialogProps) {
  const [ano, setAno] = useState<string>(new Date().getFullYear().toString());
  const [pct, setPct] = useState<string>('');
  const [fonte, setFonte] = useState('IPC-FIPE');
  const [decreto, setDecreto] = useState('');
  const [vigencia, setVigencia] = useState<string>('');

  function handleCadastrar() {
    if (!ano || !pct || !vigencia) {
      toast.error('Campos obrigatórios', {
        description: 'Ano, índice e vigência são obrigatórios.',
      });
      return;
    }
    // Nenhuma tabela de reajuste aceita escrita hoje — não há rota de criação na
    // API. O aviso é honesto: nada foi gravado, então nada se invalida/recarrega.
    mostrarAviso(AVISO_DE_CADASTRO_INDISPONIVEL);
    onOpenChange(false);
  }

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Novo índice de reajuste"
      desktopMaxWidth="max-w-md"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleCadastrar}>Cadastrar</Button>
        </>
      }
    >
      <div className="grid gap-3 py-2">
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="reajuste-ano">Ano</Label>
            <Input
              id="reajuste-ano"
              type="number"
              value={ano}
              onChange={(e) => setAno(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="reajuste-indice">Índice (%)</Label>
            <Input
              id="reajuste-indice"
              type="number"
              step="0.0001"
              value={pct}
              onChange={(e) => setPct(e.target.value)}
              placeholder="ex.: 4.6200"
            />
          </div>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="reajuste-fonte">Fonte</Label>
          <Input id="reajuste-fonte" value={fonte} onChange={(e) => setFonte(e.target.value)} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="reajuste-decreto">Decreto / referência</Label>
          <Input
            id="reajuste-decreto"
            value={decreto}
            onChange={(e) => setDecreto(e.target.value)}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="reajuste-vigencia">Vigência inicia em</Label>
          <Input
            id="reajuste-vigencia"
            type="date"
            value={vigencia}
            onChange={(e) => setVigencia(e.target.value)}
          />
        </div>
      </div>
    </ResponsiveModal>
  );
}

interface SimulacaoDialogProps {
  ano: number;
  onClose: () => void;
}

function SimulacaoDialog({ ano, onClose }: SimulacaoDialogProps) {
  const { data: linhas, isLoading, isError, error, refetch } = useSimulacaoDeReajuste(ano);

  function aplicar() {
    // Aplicar encerraria a vigência das tabelas atuais e criaria novas versões,
    // mas não há rota para isso na API ainda. Por isso só o aviso aparece e o
    // modal fica como estava: esconder o botão ou trocar "Cancelar" por "Fechar"
    // daria cara de reajuste aplicado (CLAUDE.md, "Proibido": o mock é só de
    // leitura, nunca fingindo gravar).
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  }

  return (
    <ResponsiveModal
      open
      onOpenChange={(aberto) => !aberto && onClose()}
      title={`Simulação de reajuste · ${ano}`}
      description="Confira os valores propostos antes de aplicar. A aplicação encerra a vigência das versões atuais."
      desktopMaxWidth="max-w-3xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={aplicar} disabled={!linhas?.length}>
            Aplicar reajuste {ano}
          </Button>
        </>
      }
    >
      {isError ? (
        <ErrorState
          variant="load-failed"
          description={
            error?.aviso ??
            'Não foi possível simular o reajuste agora. Verifique sua conexão e tente novamente.'
          }
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : isLoading ? (
        <Spinner />
      ) : !linhas || linhas.length === 0 ? (
        <EmptyState
          icon={TrendingUp}
          title="Nada para simular"
          description="Não há tabela de preços ou valor base para reajustar neste ano."
        />
      ) : (
        <div className="max-h-[60vh] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tipo</TableHead>
                <TableHead>Alvo</TableHead>
                <TableHead>Campo</TableHead>
                <TableHead className="text-right">Atual</TableHead>
                <TableHead className="text-right">Proposto</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {linhas.map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="text-xs">{l.tipo_alvo}</TableCell>
                  <TableCell className="text-xs">{l.alvo_nome}</TableCell>
                  <TableCell className="text-xs">{l.campo}</TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatarDinheiro(l.valor_atual)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs text-success">
                    {formatarDinheiro(l.valor_novo)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </ResponsiveModal>
  );
}

export default ReajusteAnualPage;
